import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle2, MapPin, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact InsureGenie — Insurance Questions for Indian Customers",
    description: "Reach InsureGenie for questions about health insurance, term life, 80D tax deductions or partnering with our AI insurance platform. We reply within 1 working day.",
  });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message received. We'll get back to you within 48 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout eyebrow="Talk to us" title="We'd love to hear from you.">
      <div className="not-prose grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Got a feature request, a partnership idea, or feedback on an answer? Send it our way.
            We read every message and respond within two working days.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm">Email</div>
                <a href="mailto:hello@insuregenie.ai" className="text-sm text-muted-foreground hover:text-foreground">hello@insuregenie.ai</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                <MessageSquareText className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm">Support</div>
                <a href="mailto:support@insuregenie.ai" className="text-sm text-muted-foreground hover:text-foreground">support@insuregenie.ai</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm">Based in</div>
                <span className="text-sm text-muted-foreground">Bengaluru, India · Remote-first</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div data-testid="contact-success" className="bg-card border border-border rounded-2xl p-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-semibold">Message sent.</h3>
              <p className="mt-2 text-sm text-muted-foreground">We&apos;ll respond to your inquiry within 48 hours.</p>
              <Button
                data-testid="contact-send-another-btn"
                onClick={() => setSent(false)}
                variant="outline"
                className="mt-5 rounded-full"
              >
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-card border border-border rounded-2xl p-6 sm:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</Label>
                  <Input data-testid="contact-name-input" required value={form.name} onChange={onChange("name")} className="mt-1.5 h-11 rounded-xl" placeholder="Your full name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input data-testid="contact-email-input" type="email" required value={form.email} onChange={onChange("email")} className="mt-1.5 h-11 rounded-xl" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subject</Label>
                <Input data-testid="contact-subject-input" required value={form.subject} onChange={onChange("subject")} className="mt-1.5 h-11 rounded-xl" placeholder="What is it about?" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Message</Label>
                <Textarea data-testid="contact-message-input" required minLength={10} value={form.message} onChange={onChange("message")} className="mt-1.5 min-h-[140px] rounded-xl resize-y" placeholder="Tell us what's on your mind…" />
              </div>
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send message <Send className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
